import { createFileRoute, useBlocker, useLocation } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GameConfigStep } from '@/components/features/sessions/create/GameConfigStep';
import { PlayerSelectionStep } from '@/components/features/sessions/create/PlayerSelectionStep';
import { SessionSettingsStep } from '@/components/features/sessions/create/SessionSettingsStep';
import { TeamConfigurationStep } from '@/components/features/sessions/create/TeamConfigurationStep';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Stepper, type Step } from '@/components/ui/stepper';
import { listGameDefinitions } from '@/db/game-definitions';
import { listPlayers, getPlayer, updatePlayer, validatePlayersExist } from '@/db/players';
import { addSessionEventsBatch } from '@/db/session-events';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSessionTemplates } from '@/hooks/useSessionTemplates';
import { handleBackgroundError, handleUserFacingError } from '@/lib/error-handler';
import { enqueueSessionSync } from '@/lib/realtime-sync/plugins/session';
import { toastError } from '@/lib/toast-helpers';
import type {
  GameConfig,
  GameDefinition,
  Player,
  PlayerId,
  SessionStartedEvent,
  SessionTemplate,
} from '@/schemas';
import {
  GameConfigSchema,
  createRoundId,
  createSessionId,
  createTeamId,
  parseTeamId,
} from '@/schemas';
import { useAuthStore } from '@/stores';
import { assignPlayerColor } from '@/stores/player-utils';

type SessionNewSearch = { playerIds?: PlayerId[]; step?: number };

type PrefilledData = {
  gameConfig?: GameConfig;
  teamConfiguration?: readonly TeamConfig[];
  sessionName?: string;
  sessionNotes?: string;
  selectedPreset?: GameDefinition;
  playerIds?: readonly PlayerId[];
  template?: unknown; // SessionTemplate - kept as unknown to avoid circular dependency
};

export const Route = createFileRoute('/sessions/new')({
  component: NewSessionPage,
  validateSearch: (search: Record<string, unknown>): SessionNewSearch => {
    let rawStep: number | undefined;
    if (typeof search.step === 'number') {
      rawStep = search.step;
    } else if (typeof search.step === 'string') {
      rawStep = Number.parseInt(search.step, 10);
    }
    const step = Number.isFinite(rawStep) ? Math.trunc(rawStep as number) : undefined;
    return {
      playerIds: search.playerIds as PlayerId[] | undefined,
      step: step && step >= 1 ? Math.min(step, 4) : undefined,
    };
  },
});

interface TeamConfig {
  readonly id: string;
  readonly presetId?: string;
  readonly name?: string;
  readonly playerIds: readonly PlayerId[];
}

function NewSessionPage() {
  const navigate = Route.useNavigate();
  const { t } = useTranslation('sessions');
  const { t: tCommon } = useTranslation('common');
  const { playerIds: initialPlayerIds, step: searchStep } = Route.useSearch();
  const location = useLocation();
  const prefilledData = (location.state as PrefilledData | undefined) ?? {};
  const userId = useAuthStore((state) => state.userId);

  const initialGameConfigValue: GameConfig = prefilledData.gameConfig ?? {
    hasRounds: false,
    winCondition: 'mostPoints',
    roundTargetPoints: undefined,
    gameTargetPoints: undefined,
    targetRounds: undefined,
    teamScoringType: undefined,
    version: 1,
  };
  const normalizedInitialStep = normalizeStep(
    searchStep,
    Boolean(initialGameConfigValue.teamScoringType),
  );

  // Wizard state
  const [currentStep, setCurrentStepState] = useState(normalizedInitialStep);
  const [games, setGames] = useState<GameDefinition[]>([]);
  const [gameConfig, setGameConfig] = useState<GameConfig>(initialGameConfigValue);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<readonly PlayerId[]>(
    initialPlayerIds || prefilledData.playerIds || [],
  );
  const [teamConfiguration, setTeamConfiguration] = useState<readonly TeamConfig[]>(
    prefilledData.teamConfiguration || [],
  );
  const [sessionName, setSessionName] = useState(prefilledData.sessionName || '');
  const [sessionNotes, setSessionNotes] = useState(prefilledData.sessionNotes || '');
  const [error, setError] = useState<string | undefined>();
  const [isCreating, setIsCreating] = useState(false);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<GameDefinition | undefined>(
    prefilledData.selectedPreset,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | undefined>(
    prefilledData.template as SessionTemplate | undefined,
  );
  const { sessionTemplates } = useSessionTemplates();
  // Original values before any template is applied
  const originalGameConfigRef = useRef(GameConfigSchema.parse(initialGameConfigValue));
  const originalPlayerIdsRef = useRef(initialPlayerIds || prefilledData.playerIds || []);
  const originalTeamConfigRef = useRef(prefilledData.teamConfiguration || []);
  const originalSessionNameRef = useRef(prefilledData.sessionName || '');
  // Current initial values (may be updated by template)
  const initialGameConfigRef = useRef(GameConfigSchema.parse(initialGameConfigValue));
  const initialPlayerIdsRef = useRef(initialPlayerIds || prefilledData.playerIds || []);
  const initialTeamConfigRef = useRef(prefilledData.teamConfiguration || []);
  const initialSessionNameRef = useRef(prefilledData.sessionName || '');
  const initialSessionNotesRef = useRef(prefilledData.sessionNotes || '');
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const pendingCancelRef = useRef<(() => void) | undefined>(undefined);

  // Wizard steps
  const steps: Step[] = [
    { label: t('create.step1') },
    { label: t('create.step2') },
    { label: t('create.step3') },
    { label: t('create.step4') },
  ];

  // Validate and narrow config to avoid unsafe access in lints
  const currentConfig: GameConfig = GameConfigSchema.parse(gameConfig);
  const hasTeams = Boolean(currentConfig.teamScoringType);
  const isGameConfigDirty = useMemo(
    () => !areGameConfigsEqual(initialGameConfigRef.current, currentConfig),
    [currentConfig],
  );

  const areTeamConfigsEqual = (a: readonly TeamConfig[], b: readonly TeamConfig[]): boolean => {
    if (a.length !== b.length) return false;
    return a.every((teamA, index) => {
      const teamB = b[index];
      return (
        teamA.id === teamB.id &&
        teamA.presetId === teamB.presetId &&
        teamA.name === teamB.name &&
        teamA.playerIds.length === teamB.playerIds.length &&
        teamA.playerIds.every((id) => teamB.playerIds.includes(id))
      );
    });
  };

  const hasUnsavedChanges = useMemo(() => {
    const sortStrings = (arr: readonly string[]) => [...arr].toSorted((a, b) => a.localeCompare(b));
    return (
      isGameConfigDirty ||
      JSON.stringify(sortStrings(selectedPlayerIds)) !==
        JSON.stringify(sortStrings(initialPlayerIdsRef.current)) ||
      !areTeamConfigsEqual(teamConfiguration, initialTeamConfigRef.current) ||
      sessionName.trim() !== initialSessionNameRef.current.trim() ||
      sessionNotes.trim() !== initialSessionNotesRef.current.trim()
    );
  }, [isGameConfigDirty, selectedPlayerIds, teamConfiguration, sessionName, sessionNotes]);

  const shouldConfirmExit = hasUnsavedChanges && !isCreating;

  useEffect(() => {
    const sanitized = normalizeStep(searchStep, hasTeams);
    if (sanitized !== currentStep) {
      setCurrentStepState(sanitized);
    }
  }, [searchStep, hasTeams, currentStep]);

  const goToStep = useCallback(
    (step: number, options?: { replace?: boolean }) => {
      const sanitized = normalizeStep(step, hasTeams);
      setCurrentStepState(sanitized);
      void navigate({
        replace: options?.replace ?? false,
        search: (prev) => ({
          ...prev,
          step: sanitized === 1 ? undefined : sanitized,
        }),
      });
    },
    [hasTeams, navigate],
  );

  useEffect(() => {
    if (!hasTeams && currentStep === 3) {
      goToStep(2, { replace: true });
    }
  }, [hasTeams, currentStep, goToStep]);

  const exitBlocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!shouldConfirmExit) {
        return false;
      }

      const currentRouteId = (current as { routeId?: string })?.routeId;

      const nextRouteId = (next as { routeId?: string })?.routeId;
      const isSameRoute = currentRouteId === nextRouteId;
      return !isSameRoute;
    },
    enableBeforeUnload: shouldConfirmExit,
    disabled: !shouldConfirmExit,
    withResolver: true,
  });

  useEffect(() => {
    if (exitBlocker.status === 'blocked') {
      setIsExitDialogOpen(true);
    }
  }, [exitBlocker.status]);

  useDocumentTitle(`${t('create.title')} - ${tCommon('app.name', 'One Score')}`);

  const loadGames = useCallback(async () => {
    try {
      const gameList = await listGameDefinitions();
      setGames(gameList);
    } catch (error: unknown) {
      handleBackgroundError(error, { operation: 'loadGames' });
    } finally {
      // no-op
    }
  }, []);

  const loadAllPlayers = useCallback(async () => {
    try {
      const players = await listPlayers();
      setAllPlayers(players);
    } catch (error: unknown) {
      handleBackgroundError(error, { operation: 'loadAllPlayers' });
    }
  }, []);

  const fetchMissingPlayers = useCallback(
    async (missingIds: readonly PlayerId[], currentPlayers: Player[]) => {
      try {
        const playerPromises = missingIds.map((id) => getPlayer(id));
        const players = await Promise.all(playerPromises);
        const validPlayers = players.filter((p): p is Player => p !== undefined && !p.deletedAt);

        if (validPlayers.length === 0) return [];

        const currentIds = new Set(currentPlayers.map((p) => p.id));
        return validPlayers.filter((p) => !currentIds.has(p.id));
      } catch (error) {
        handleBackgroundError(error, { operation: 'fetchMissingPlayers', playerIds: missingIds });
        return [];
      }
    },
    [],
  );

  // no-op

  // Load games and players on mount
  useEffect(() => {
    void loadGames();
    void loadAllPlayers();
  }, [loadGames, loadAllPlayers]);

  // Reload players when window regains focus (for cross-tab updates and newly created players)
  useEffect(() => {
    const handleFocus = () => {
      void loadAllPlayers();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadAllPlayers]);

  // Fetch any missing players that are selected but not in allPlayers
  useEffect(() => {
    if (selectedPlayerIds.length === 0) return;

    const existingPlayerIds = new Set(allPlayers.map((p) => p.id));
    const missingPlayerIds = selectedPlayerIds.filter((id) => !existingPlayerIds.has(id));

    if (missingPlayerIds.length === 0) return;

    let cancelled = false;

    async function handleFetchMissing() {
      const newPlayers = await fetchMissingPlayers(missingPlayerIds, allPlayers);

      if (cancelled || newPlayers.length === 0) return;

      setAllPlayers((prev) => [...prev, ...newPlayers]);
    }

    void handleFetchMissing();

    return () => {
      cancelled = true;
    };
  }, [selectedPlayerIds, allPlayers, fetchMissingPlayers]);

  // No auto-select; step 1 is the config form

  // Load selected game definition when gameId changes
  // no-op

  // No game select handler needed

  const handleNext = () => {
    if (validateCurrentStep()) {
      // If on step 2 and game requires teams, go to step 3
      // If on step 2 and game doesn't require teams, go to step 4
      // Otherwise just increment
      if (currentStep === 2) {
        const hasTeams = Boolean(currentConfig.teamScoringType);
        if (hasTeams) {
          goToStep(3);
        } else {
          goToStep(4);
        }
      } else {
        goToStep(Math.min(currentStep + 1, 4));
      }
    }
  };

  const handleBack = () => {
    // If on step 4 and game doesn't require teams, go to step 2 (skip step 3)
    // Otherwise just decrement
    if (currentStep === 4) {
      const hasTeams = Boolean(currentConfig.teamScoringType);
      goToStep(hasTeams ? 3 : 2);
    } else {
      goToStep(Math.max(currentStep - 1, 1));
    }
  };

  const handleStepClick = (clickedVisualStep: number) => {
    // clickedVisualStep is the step number as shown in the UI (1-3 if no teams, 1-4 if teams)
    const hasTeams = Boolean(currentConfig.teamScoringType);

    // Map visual step to actual step number
    let actualStep = clickedVisualStep;
    if (!hasTeams && clickedVisualStep >= 3) {
      // Without teams: visual steps are [1, 2, 4], actual steps are [1, 2, 4]
      // So visual step 3 maps to actual step 4
      actualStep = 4;
    }

    // Allow clicking on step 1 always, step 2 if game is selected, step 4 if players are selected, or steps that have been visited
    const canClickStep1 = actualStep === 1;
    const canClickStep2 = actualStep === 2; // config is always available
    const canClickStep4 = actualStep === 4 && selectedPlayerIds.length > 0;
    const canClickVisited = actualStep <= currentStep;

    if (canClickStep1 || canClickStep2 || canClickStep4 || canClickVisited) {
      goToStep(actualStep);
    }
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1: {
        return Boolean(currentConfig.winCondition);
      }
      case 2: {
        return selectedPlayerIds.length > 0;
      }
      case 3: {
        // Only validate if we have teams
        if (!currentConfig.teamScoringType) return true;

        const allPlayersAssigned = selectedPlayerIds.every((playerId) =>
          teamConfiguration.some((team) => team.playerIds.includes(playerId)),
        );
        const allTeamsValid = teamConfiguration.every((team) => team.playerIds.length > 0);
        return allPlayersAssigned && allTeamsValid;
      }
      case 4: {
        return true; // Step 4 validation is handled in onSubmit
      }
      default: {
        return false;
      }
    }
  };

  const handleSubmit = async () => {
    setIsCreating(true);
    setError(undefined);

    try {
      // Validate all selected players exist before creating session
      // This prevents creating a session with invalid player references
      if (selectedPlayerIds.length > 0) {
        await validatePlayersExist([...selectedPlayerIds]);
      }

      // Generate IDs
      const sessionId = createSessionId();
      const roundId = createRoundId();

      // Load players and ensure colors
      const playersWithColors: Player[] = [];
      const allPlayers: Player[] = [];

      for (const playerId of selectedPlayerIds) {
        let player = await getPlayer(playerId);
        if (!player || player.deletedAt) continue;

        if (!player.color) {
          const color = assignPlayerColor(allPlayers);
          await updatePlayer(playerId, { color });
          player = { ...player, color };
        }

        allPlayers.push(player);
        playersWithColors.push(player);
      }

      const playerJoinedEvents = playersWithColors.map((player) => ({
        sessionId,
        userId,
        type: 'PlayerJoined' as const,
        payload: {
          playerId: player.id,
          name: player.name,
          nickname: player.nickname,
          color: player.color,
          entryRound: 1,
        },
      }));

      // Generate default session name if not provided
      const finalSessionName =
        sessionName.trim() ||
        (() => {
          const date = new Date();
          const formatted = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }).format(date);
          // Use game preset name if available, otherwise use "Session"
          const prefix = selectedPreset?.name || 'Session';
          return `${prefix} - ${formatted}`;
        })();

      // Build team configuration if required
      const finalTeamConfiguration: SessionStartedEvent['payload']['teamConfiguration'] =
        currentConfig.teamScoringType && teamConfiguration.length > 0
          ? {
              teams: teamConfiguration.map((team) => ({
                id: team.presetId ? parseTeamId(team.presetId) : createTeamId(),
                presetId: team.presetId ? parseTeamId(team.presetId) : undefined,
                name: team.name,
                nickname: undefined,
                avatar: undefined,
                color: undefined,
                playerIds: [...team.playerIds],
              })),
            }
          : undefined;

      const initialEvents = [
        {
          sessionId,
          userId,
          type: 'SessionStarted' as const,
          payload: {
            name: finalSessionName,
            notes: sessionNotes.trim() || undefined,
            gameConfigSnapshot: currentConfig,
            playerIds: selectedPlayerIds,
            teamConfiguration: finalTeamConfiguration,
          },
        },
        ...playerJoinedEvents,
        {
          sessionId,
          userId,
          type: 'RoundStarted' as const,
          payload: {
            roundId,
            number: 1,
            name: undefined,
          },
        },
      ];

      const sessionEventsPromise = (async () => {
        // Create events locally
        await addSessionEventsBatch(initialEvents);
        enqueueSessionSync(sessionId);
      })();

      const loadingId = toast.loading(t('sessions.creating', 'Creating session...'));
      try {
        await sessionEventsPromise;
      } catch (error_: unknown) {
        toastError(
          error_ instanceof Error
            ? error_.message
            : t('sessions.createError', 'Failed to create session'),
        );
        setIsCreating(false);
        return;
      } finally {
        toast.dismiss(loadingId);
      }
      // Navigate to session page after successful creation
      void navigate({ to: '/sessions/$sessionId', params: { sessionId } });
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('sessions.createError', 'Failed to create session'),
        { operation: 'createSession', step: currentStep },
      );
      setError(errorMessage);
      toastError(errorMessage);
      setIsCreating(false);
    }
  };

  const handleTemplateRemove = useCallback(() => {
    setSelectedTemplate(undefined);
    // Reset to original values (before template was applied)
    setGameConfig({ ...originalGameConfigRef.current });
    setSelectedPlayerIds([...originalPlayerIdsRef.current]);
    setTeamConfiguration([...originalTeamConfigRef.current]);
    setSessionName(originalSessionNameRef.current);
    setSelectedPreset(undefined);
    // Reset initial refs to original values
    initialGameConfigRef.current = { ...originalGameConfigRef.current };
    initialPlayerIdsRef.current = [...originalPlayerIdsRef.current];
    initialTeamConfigRef.current = [...originalTeamConfigRef.current];
    initialSessionNameRef.current = originalSessionNameRef.current;
  }, []);

  const handleTemplateSelect = useCallback(
    async (template: SessionTemplate) => {
      try {
        // Filter out deleted players
        const validPlayerIds = template.playerIds.filter((playerId) => {
          const player = allPlayers.find((p) => p.id === playerId);
          return player && !player.deletedAt;
        });

        if (validPlayerIds.length === 0) {
          toast.error(
            t(
              'create.gameConfig.template.warning.missingPlayers',
              'Some players from this template are no longer available',
            ),
          );
          return;
        }

        // Show warning if some players were removed
        if (validPlayerIds.length < template.playerIds.length) {
          toast.warning(
            t(
              'create.gameConfig.template.warning.missingPlayers',
              'Some players from this template are no longer available',
            ),
          );
        }

        // Clear selected preset when applying template
        setSelectedPreset(undefined);

        // Apply game config
        setGameConfig(template.gameConfig);

        // Apply player IDs
        setSelectedPlayerIds(validPlayerIds);

        // Apply team configuration if present and valid
        if (template.teamConfiguration?.teams) {
          // Filter teams to only include valid players
          const validTeams = template.teamConfiguration.teams
            .map((team) => ({
              id: team.id,
              presetId: team.presetId,
              name: team.name,
              playerIds: team.playerIds.filter((pid) => validPlayerIds.includes(pid)),
            }))
            .filter((team) => team.playerIds.length > 0);

          if (validTeams.length > 0 && template.gameConfig.teamScoringType) {
            setTeamConfiguration(validTeams);
          } else {
            setTeamConfiguration([]);
          }
        } else {
          setTeamConfiguration([]);
        }

        // Apply default session name if present
        if (template.defaultSessionName) {
          setSessionName(template.defaultSessionName);
        }

        // Store template
        setSelectedTemplate(template);

        // Update initial refs for tracking changes
        initialGameConfigRef.current = GameConfigSchema.parse(template.gameConfig);
        initialPlayerIdsRef.current = validPlayerIds;
        initialTeamConfigRef.current =
          template.teamConfiguration?.teams
            .map((team) => ({
              id: team.id,
              presetId: team.presetId,
              name: team.name,
              playerIds: team.playerIds.filter((pid) => validPlayerIds.includes(pid)),
            }))
            .filter((team) => team.playerIds.length > 0) || [];
        initialSessionNameRef.current = template.defaultSessionName || '';

        // Fetch any missing players
        const existingPlayerIds = new Set(allPlayers.map((p) => p.id));
        const missingPlayerIds = validPlayerIds.filter((id) => !existingPlayerIds.has(id));

        if (missingPlayerIds.length > 0) {
          const newPlayers = await fetchMissingPlayers(missingPlayerIds, allPlayers);
          if (newPlayers.length > 0) {
            setAllPlayers((prev) => [...prev, ...newPlayers]);
          }
        }
      } catch (error: unknown) {
        handleBackgroundError(error, { operation: 'handleTemplateSelect' });
        toastError(t('sessions.createError', 'Failed to apply template'));
      }
    },
    [allPlayers, fetchMissingPlayers, t],
  );

  // Dynamic step calculation based on team requirements
  const actualSteps = hasTeams ? steps : steps.filter((_, i) => i !== 2); // Skip team step if not needed

  // Adjust current step if we skipped team configuration
  const adjustedStep = hasTeams || currentStep <= 2 ? currentStep : currentStep + 1;

  // Determine which steps are clickable
  // Step 1: Always available
  // Step 2: Available if game is selected
  // Step 3: Available if it exists (teams) AND players are selected
  // Step 4: Available if players are selected
  const clickableSteps: boolean[] = actualSteps.map((_, index) => {
    const visualStep = index + 1;
    if (visualStep === 1) {
      return true; // Always available
    }
    if (visualStep === 2) {
      return true; // Config step is always available
    }
    if (visualStep === 3) {
      return hasTeams && selectedPlayerIds.length > 0; // Available if teams exist and players selected
    }
    // Step 4 or later: available if players selected
    return selectedPlayerIds.length > 0;
  });

  return (
    <div className="w-full max-w-6xl space-y-8 px-4 mx-auto py-8">
      {/* Header */}
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold text-foreground">{t('create.title')}</h1>
        <p className="text-muted-foreground">{t('create.subtitle')}</p>
      </div>

      {/* Stepper */}
      <Stepper
        steps={actualSteps}
        currentStep={adjustedStep}
        onStepClick={handleStepClick}
        clickableSteps={clickableSteps}
      />

      {/* Step Content */}
      <div className="min-h-[400px] transition-all duration-300">
        {currentStep === 1 && (
          <GameConfigStep
            config={currentConfig}
            onChange={setGameConfig}
            presets={games}
            onPresetSelect={setSelectedPreset}
            initialPresetId={selectedPreset ? String(selectedPreset.id) : undefined}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={(template) => {
              void handleTemplateSelect(template);
            }}
            onTemplateRemove={handleTemplateRemove}
            templates={sessionTemplates}
          />
        )}

        {currentStep === 2 && (
          <PlayerSelectionStep
            selectedPlayerIds={selectedPlayerIds}
            onSelectionChange={setSelectedPlayerIds}
          />
        )}

        {currentStep === 3 && currentConfig.teamScoringType && (
          <TeamConfigurationStep
            selectedPlayers={allPlayers.filter((p) => selectedPlayerIds.includes(p.id))}
            teamConfiguration={teamConfiguration}
            onConfigurationChange={setTeamConfiguration}
          />
        )}

        {currentStep === 4 && (
          <SessionSettingsStep
            gameConfig={currentConfig}
            selectedPlayers={allPlayers.filter((p) => selectedPlayerIds.includes(p.id))}
            teamConfiguration={teamConfiguration}
            sessionName={sessionName}
            sessionNotes={sessionNotes}
            onNameChange={setSessionName}
            onNotesChange={setSessionNotes}
            onEditStep={goToStep}
          />
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        {currentStep > 1 && (
          <Button type="button" variant="outline" onClick={handleBack} disabled={isCreating}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('create.back')}
          </Button>
        )}

        <div className={`flex gap-2 ${currentStep === 1 ? 'ml-auto' : ''}`}>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (hasUnsavedChanges) {
                pendingCancelRef.current = () => {
                  void navigate({ to: '/' });
                };
                setIsExitDialogOpen(true);
              } else {
                void navigate({ to: '/' });
              }
            }}
            disabled={isCreating}
          >
            {t('create.cancel')}
          </Button>

          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!validateCurrentStep() || isCreating}
            >
              {t('create.next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={isCreating || !validateCurrentStep()}
            >
              {isCreating ? t('create.creating') : t('create.startSession')}
            </Button>
          )}
        </div>
      </div>

      <AlertDialog
        open={isExitDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsExitDialogOpen(false);
            exitBlocker.reset?.();
            pendingCancelRef.current = undefined;
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('create.exitDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'create.exitDialog.description',
                'You have unsaved changes in your session form. Leave without saving?',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setIsExitDialogOpen(false);
                exitBlocker.reset?.();
                pendingCancelRef.current = undefined;
              }}
            >
              {t('create.exitDialog.stay')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsExitDialogOpen(false);
                if (pendingCancelRef.current) {
                  pendingCancelRef.current();
                  pendingCancelRef.current = undefined;
                } else {
                  exitBlocker.proceed?.();
                }
              }}
            >
              {t('create.exitDialog.leave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function normalizeStep(step: number | undefined, hasTeams: boolean): number {
  if (!step || Number.isNaN(step)) {
    return 1;
  }
  const clamped = Math.min(Math.max(Math.trunc(step), 1), 4);
  if (!hasTeams && clamped === 3) {
    return 2;
  }
  return clamped;
}

function areGameConfigsEqual(a: GameConfig, b: GameConfig): boolean {
  return (
    a.hasRounds === b.hasRounds &&
    a.winCondition === b.winCondition &&
    a.roundTargetPoints === b.roundTargetPoints &&
    a.gameTargetPoints === b.gameTargetPoints &&
    a.targetRounds === b.targetRounds &&
    a.teamScoringType === b.teamScoringType &&
    a.version === b.version
  );
}
